import { AnimatePresence, motion } from "framer-motion";
import { Maximize2, Pause, Play, X } from "lucide-react";
import { fmtTime, usePlayer } from "@/context/PlayerContext";
import { mediaUrl } from "@/lib/api";
import { ShareTrackButton, WhatsAppTrackButton } from "./ShareTrackButton";
import { SocialIcon } from "./SocialIcons";
import { Waveform } from "./Waveform";

export const PlayerBar = () => {
  const { track, playing, time, dur, toggle, seek, setModalOpen, close } = usePlayer();

  const onSeek = (e) => {
    const v = Number(e.target.value);
    seek(v);
    e.target.style.setProperty("--fill", `${(v / dur) * 100}%`);
  };

  return (
    <AnimatePresence>
      {track && (
        <motion.div
          initial={{ y: 90, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 90, opacity: 0 }}
          transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
          className="fixed bottom-0 inset-x-0 z-40 glass-panel border-t border-gold-400/20"
          data-testid="global-audio-player"
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-8 py-3 flex items-center gap-4">
            <img
              src={mediaUrl(track.cover_url)}
              alt={track.title}
              className={`w-12 h-12 rounded-md object-cover border border-gold-400/30 ${playing ? "animate-glow" : ""}`}
              data-testid="player-cover"
            />
            <div className="min-w-0 w-32 sm:w-44">
              <p className="font-serif text-sm text-slate-100 truncate" data-testid="player-track-title">
                {track.title}
              </p>
              <p className="text-[11px] text-gold-400/80 tracking-widest uppercase">60s preview</p>
            </div>
            <button
              onClick={toggle}
              data-testid="player-play-toggle"
              aria-label={playing ? "Pause" : "Play"}
              className="w-10 h-10 shrink-0 rounded-full flex items-center justify-center text-navy-950 transition-transform hover:scale-105"
              style={{ background: "linear-gradient(135deg,#F59E0B,#D4AF37 50%,#B38728)" }}
            >
              {playing ? <Pause size={16} fill="currentColor" /> : <Play size={16} fill="currentColor" className="ml-0.5" />}
            </button>
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <span className="text-[11px] text-slate-400 tabular-nums w-8 hidden sm:block" data-testid="player-time-elapsed">
                {fmtTime(time)}
              </span>
              <input
                type="range"
                className="seek flex-1 hidden sm:block"
                min={0}
                max={dur}
                step={0.1}
                value={time}
                onChange={onSeek}
                data-testid="player-seek"
                aria-label="Seek"
              />
              <span className="text-[11px] text-slate-400 tabular-nums w-8 hidden sm:block">{fmtTime(dur)}</span>
              <Waveform playing={playing} bars={18} className="h-8 flex-1 min-w-0 hidden md:block" />
            </div>
            {track.spotify_url && (
              <a
                href={track.spotify_url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="player-spotify-btn"
                aria-label="Listen on Spotify"
                title="Listen on Spotify"
                className="shrink-0 inline-flex items-center justify-center rounded-full border border-gold-400/40 p-2 text-gold-200 hover:bg-gold-400/10 transition-colors"
              >
                <SocialIcon name="spotify" size={14} />
              </a>
            )}
            {track.apple_url && (
              <a
                href={track.apple_url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="player-apple-btn"
                aria-label="Listen on Apple Music"
                title="Listen on Apple Music"
                className="shrink-0 inline-flex items-center justify-center rounded-full border border-gold-400/40 p-2 text-gold-200 hover:bg-gold-400/10 transition-colors"
              >
                <SocialIcon name="apple" size={14} />
              </a>
            )}
            <ShareTrackButton track={track} compact testid="player-share-btn" />
            <WhatsAppTrackButton track={track} testid="player-whatsapp-btn" />
            <button
              onClick={() => setModalOpen(true)}
              data-testid="player-expand"
              aria-label="Open track details"
              className="text-slate-400 hover:text-gold-300 transition-colors p-2"
            >
              <Maximize2 size={17} />
            </button>
            <button
              onClick={close}
              data-testid="player-close"
              aria-label="Close player"
              className="text-slate-500 hover:text-slate-200 transition-colors p-2"
            >
              <X size={17} />
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
